import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api, tokens } from './client';
import type { UserRole } from './types';

export interface AdminIdentity {
  id: string;
  email: string;
  role: UserRole;
}

interface AuthState {
  admin: AdminIdentity | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [loading, setLoading] = useState(true);

  // A stored token is only useful if it still belongs to an admin — role can
  // have been revoked since it was issued, so the panel asks rather than
  // trusting what it saved.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!tokens.access) {
        setLoading(false);
        return;
      }

      try {
        const { admin: identity } = await api<{ admin: AdminIdentity }>('/v1/admin/me');
        if (!cancelled) setAdmin(identity);
      } catch {
        tokens.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void restore();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api<{ tokens: { accessToken: string; refreshToken: string } }>(
      '/v1/auth/sign-in',
      { method: 'POST', body: { email, password } },
    );

    tokens.set(result.tokens.accessToken, result.tokens.refreshToken);

    try {
      const { admin: identity } = await api<{ admin: AdminIdentity }>('/v1/admin/me');
      setAdmin(identity);
    } catch (error) {
      // Correct password, wrong privileges: say so plainly rather than
      // leaving a signed-in session that cannot load a single screen.
      tokens.clear();
      if (error instanceof ApiError && error.status === 403) {
        throw new ApiError(403, 'FORBIDDEN', 'That account is not an admin.');
      }
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = tokens.refresh;

    try {
      if (refreshToken) await api('/v1/auth/sign-out', { method: 'POST', body: { refreshToken } });
    } catch {
      // Signing out locally matters more than the server acknowledging it.
    }

    tokens.clear();
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({ admin, loading, signIn, signOut }),
    [admin, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
